import { isInPolygon } from '@wix/wixrest-utils/dist/GeoUtils';
import moment, { Moment } from 'moment-timezone';
import _ from 'lodash';
import { isAvailableOn } from 'availability';
import {
  DEFAULT_ADDRESS,
  DEFAULT_ADDRESS_SHORT,
  EMPTY_ADDRESS,
  NO_ADDRESS,
} from '../constants';
import { getAvailableDeliveryInfos } from './dateTimeLogic';
import { Address } from '../types/Address';
import {
  DeliveryDispatchInfo,
  DispatchInfo,
  PickupDispatchInfo,
  Restaurant,
} from '../types/Restaurant';

export interface GetIdealDeliveryAreaArgs {
  totalOrderPrice?: number;
  dispatchTime?: number;
  address: Address;
  restaurant: Pick<Restaurant, 'timezone' | 'deliveryInfos'>;
}

export interface IdealDeliveryArea {
  isValid: boolean;
  dispatchInfo?: DispatchInfo;
  reason?: ValidateAddressReason;
}

export interface ValidateAddressReason {
  type: AddressMismatchReason;
  minPrice?: number;
}

export function isAddress(address: any): address is Address {
  return (
    Boolean(address?.formatted) &&
    typeof address?.latLng?.lat === 'number' &&
    typeof address?.latLng?.lng === 'number'
  );
}

export function validateAddress({
  address,
  restaurant,
  totalOrderPrice,
  dispatchTime,
}: {
  address?: Address;
  restaurant: Restaurant;
  totalOrderPrice?: number;
  dispatchTime?: number;
}): ValidateAddressReason | undefined {
  if (!isAddress(address)) {
    return { type: 'invalid-address' };
  } else if (getIsPinpointError(address, restaurant)) {
    return { type: 'pinpoint-error' };
  } else {
    const { reason: deliveryAreaError } = getIdealDeliveryArea({
      totalOrderPrice,
      restaurant,
      address,
      dispatchTime,
    });
    return deliveryAreaError;
  }
}

export function getIsPinpointError(
  address: Address,
  restaurant: Pick<Restaurant, 'deliveriesInfo'>,
) {
  const { approximateAddresses } = restaurant.deliveriesInfo;
  return approximateAddresses === 'disallow' && !address.number;
}

export function getMinOrderPriceForIdealArea({
  totalOrderPrice,
  dispatchTime,
  address,
  restaurant,
}: GetIdealDeliveryAreaArgs): number | undefined {
  // First, get all delivery areas matching the address lag/lng
  const inBounds = getAllInBounds({
    restaurant,
    address,
    dispatchTime,
    totalOrderPrice,
  });

  // Now, get the best (=cheapest) available delivery area, if exists.
  const idealDeliveryArea: ValidatedDeliveryDispatchInfo | undefined = _(
    inBounds,
  )
    .filter((v) => v.reasons.length === 0)
    .sortBy((v) => v.info.charge || 0)
    .first();

  return idealDeliveryArea ? idealDeliveryArea.info.minOrderPrice : undefined;
}

export function getIdealDeliveryArea({
  totalOrderPrice,
  dispatchTime,
  address,
  restaurant,
}: GetIdealDeliveryAreaArgs): IdealDeliveryArea {
  // First, get all delivery areas matching the address lag/lng
  const inBounds = getAllInBounds({
    restaurant,
    address,
    dispatchTime,
    totalOrderPrice,
  });

  // Now, get the best (=cheapest) available delivery area, if exists.
  const idealDeliveryArea = _(inBounds)
    .filter((v) => v.reasons.length === 0)
    .sortBy((v) => v.info.charge || 0)
    .first();

  const isValid = Boolean(idealDeliveryArea);

  return {
    isValid,
    dispatchInfo: idealDeliveryArea?.info,
    reason: !isValid ? getMostImportantReason(inBounds) : undefined,
  };
}

type AddressMismatchReasonForLocations = Exclude<
  AddressMismatchReason,
  'minimum-price' | 'unavailable'
>;

/**
 * @returns location that can deliver to the address or a reason why not
 */
export function validateAddressForLocations<
  T extends Pick<Restaurant, 'timezone' | 'deliveryInfos' | 'deliveriesInfo'>
>(
  locations: T[],
  address: Address,
): {
  hasLocations: boolean;
  reason?: { type: AddressMismatchReasonForLocations };
  locations?: T[];
} {
  // 1. check if the address in valid
  // 2. check if the address contains address number when required by a location
  // 3. check if the locations can deliver to the address
  if (!isAddress(address)) {
    return { hasLocations: false, reason: { type: 'invalid-address' } };
  } else if (isAllLocationHavePinpointError()) {
    return { hasLocations: false, reason: { type: 'pinpoint-error' } };
  } else {
    const validLocations = locations
      .map((location) => {
        // 1. Get all delivery areas matching the address lag/lng
        // 2. Filter out out-of-bound and closed reasons
        const inBounds = getAllInBounds({
          totalOrderPrice: 0, // we don't care about totalOrderPrice
          dispatchTime: Date.now(), // we don't care about dispatchTime
          address,
          restaurant: location,
        }).filter((inBound) => {
          // address is out of bounds for this areas
          if (
            inBound.reasons.find((reason) => reason.type === 'out-of-bounds')
          ) {
            return false;
          }

          const unavailable = inBound.reasons.find(
            (reason) => reason.type === 'unavailable',
          );

          // check if the delivery area is closed or just unavailable at this time
          // if delivery area is closed, filter it out
          if (unavailable) {
            const closedException = inBound.info?.availability?.exceptions?.find(
              (exps) => exps.available === false && exps?.reason === 'closed',
            );

            if (closedException) {
              return false;
            }
          }

          // everything else we want
          return true;
        });

        // Now, get the best (=cheapest) available delivery area, if exists.
        const idealDeliveryArea = _(inBounds)
          .sortBy((v) => v.info.charge || 0)
          .first();

        const isValid = Boolean(idealDeliveryArea);

        return {
          isValid,
          reason: !isValid ? getMostImportantReason(inBounds) : undefined,
          location,
        };
      })
      .filter((location) => location.isValid);

    // at this point after we filtered the locations that cannot deliver to the address
    // if we have no location, we are sure this is because out-of-bounds reason
    if (validLocations.length === 0) {
      return { hasLocations: false, reason: { type: 'out-of-bounds' } };
    } else {
      return {
        hasLocations: true,
        locations: validLocations.map(
          (validLocation) => validLocation.location,
        ),
      };
    }
  }

  function isAllLocationHavePinpointError() {
    return (
      locations.filter((location) => !getIsPinpointError(address, location))
        .length === 0
    );
  }
}

/**
 * @returns locations that have takeout enabled
 */
export function getLocationsWithTakeout<
  T extends Pick<Restaurant, 'deliveryInfos'>
>(locations: T[]) {
  return locations.filter((locaction) => {
    const pickupDispatchInfo = locaction.deliveryInfos.find(
      (di) => di.type === 'takeout',
    );
    if (!pickupDispatchInfo || pickupDispatchInfo.inactive) {
      return false;
    }
    return true;
  });
}

export function getLocationsWithDineIn<
  T extends Pick<Restaurant, 'deliveryInfos' | 'timezone'>
>(locations: T[]) {
  return locations.filter((location) => {
    return (
      validateTakeout(location, Date.now()).isValid &&
      getPickupDispatchInfo(location.deliveryInfos)?.contactlessDineInInfo
    );
  });
}
export function getFormattedAddressWithComment(address: Address): string {
  const formattedAddress =
    address.formatted === NO_ADDRESS || address.formatted === DEFAULT_ADDRESS
      ? DEFAULT_ADDRESS_SHORT
      : address.formatted;
  if (
    !address.comment ||
    !address.properties ||
    !address.properties['com.openrest']
  ) {
    return formattedAddress;
  }

  const { addressOverride } = JSON.parse(address.properties['com.openrest']);

  if (!addressOverride) {
    return formattedAddress;
  }

  if (addressOverride === 'override_replace') {
    return address.comment;
  }

  if (addressOverride === 'override_before') {
    return `${address.comment}, ${formattedAddress}`;
  }

  if (addressOverride === 'override_after') {
    return `${formattedAddress}, ${address.comment}`;
  }

  return formattedAddress;
}

export type AddressMismatchReason =
  | 'out-of-bounds'
  | 'minimum-price'
  | 'unavailable'
  | 'invalid-address'
  | 'pinpoint-error';

function isAddressMatchingDispatchInfo(
  address: Address = EMPTY_ADDRESS,
  dispatchInfo: DeliveryDispatchInfo,
  dispatchTime: Moment,
  totalOrderPrice?: number,
) {
  const reasons: ValidateAddressReason[] = [];

  if (!isInPolygon(dispatchInfo.area.polygon, address.latLng)) {
    reasons.push({ type: 'out-of-bounds' });
  }

  const minPrice = dispatchInfo.minOrderPrice || 0;
  if (totalOrderPrice !== undefined && totalOrderPrice < minPrice) {
    reasons.push({ type: 'minimum-price', minPrice });
  }

  if (!isAvailableOn(dispatchInfo.availability, dispatchTime)) {
    reasons.push({ type: 'unavailable' });
  }

  return reasons;
}

interface ValidatedDeliveryDispatchInfo {
  info: DeliveryDispatchInfo;
  reasons: ValidateAddressReason[];
}

interface GetAllInBoundsArgs {
  restaurant: Pick<Restaurant, 'timezone' | 'deliveryInfos'>;
  dispatchTime?: number;
  totalOrderPrice?: number;
  address: Address;
}

function getAllInBounds({
  restaurant,
  dispatchTime,
  totalOrderPrice,
  address,
}: GetAllInBoundsArgs): ValidatedDeliveryDispatchInfo[] {
  const deliveries = restaurant.deliveryInfos.filter(
    (di) => di.type === 'delivery',
  ) as DeliveryDispatchInfo[];
  const validated: ValidatedDeliveryDispatchInfo[] = deliveries.map(
    (deliveryDispatchInfo) => ({
      info: deliveryDispatchInfo,
      reasons: isAddressMatchingDispatchInfo(
        address,
        deliveryDispatchInfo,
        moment(dispatchTime).tz(restaurant.timezone),
        totalOrderPrice,
      ),
    }),
  );

  return validated.filter(isInBounds);
}

function isInBounds(
  validatedDeliveryDispatchInfo: ValidatedDeliveryDispatchInfo,
) {
  return !validatedDeliveryDispatchInfo.reasons
    .map((r) => r.type)
    .includes('out-of-bounds');
}

function getMostImportantReason(
  validatedDeliveryDispatchInfos: ValidatedDeliveryDispatchInfo[],
): ValidateAddressReason | undefined {
  const weights: Record<AddressMismatchReason, number> = {
    unavailable: 100,
    'minimum-price': 50,
    'out-of-bounds': 0,
    'invalid-address': -1,
    'pinpoint-error': -1,
  };

  if (validatedDeliveryDispatchInfos.length === 0) {
    // In case no delivery areas are available at this point,
    // it means the address does not match an of the delivery areas.
    return { type: 'out-of-bounds' };
  }

  // find the delivery area with tht samllest amount of errors
  const deliveryAreaWithLeastErrors = _(validatedDeliveryDispatchInfos)
    .sortBy([(di) => di.info.minOrderPrice, (di) => di.reasons.length])
    .first();

  if (deliveryAreaWithLeastErrors) {
    // Sorting mismatch reasons by their importance, and return the first one.
    return _(deliveryAreaWithLeastErrors.reasons)
      .uniq()
      .sortBy((r) => [weights[r.type] * -1, r.minPrice || 1])
      .first();
  } else {
    return undefined;
  }
}

function getPickupDispatchInfo(deliveryInfos: DispatchInfo[]) {
  return deliveryInfos.find(
    (di) => di.type === 'takeout',
  ) as PickupDispatchInfo;
}

export function validateTakeout(
  restaurant: Pick<Restaurant, 'deliveryInfos' | 'timezone'>,
  dispatchTime: number,
) {
  const pickupDispatchInfo = restaurant.deliveryInfos.find(
    (di) => di.type === 'takeout',
  );

  if (!pickupDispatchInfo) {
    return {
      isValid: false,
      reason: 'takeout-not-supported',
    };
  }

  if (
    !isAvailableOn(
      pickupDispatchInfo.availability,
      moment(dispatchTime).tz(restaurant.timezone),
    )
  ) {
    return {
      isValid: false,
      reason: 'unavailable',
    };
  } else {
    return {
      isValid: true,
    };
  }
}

export function getMinimalOrderPriceForAllAreas(deliveryInfos: DispatchInfo[]) {
  const deliveries = getAvailableDeliveryInfos(deliveryInfos).filter(
    (di) => di.type === 'delivery',
  ) as DeliveryDispatchInfo[];

  return deliveries.length < 1 ||
    deliveries.find((del) => del.minOrderPrice === undefined)
    ? undefined
    : deliveries.sort(
        (delA, delB) => delA.minOrderPrice! - delB.minOrderPrice!,
      )[0].minOrderPrice;
}
